/**
 * ChallengeModal Component
 * Modal to send or respond to game challenges
 */

import React, { useState, useEffect } from "react";
import {
  View,
  Text,
  TouchableOpacity,
  TextInput,
  ScrollView,
  ActivityIndicator,
  Image,
} from "react-native";
import { Ionicons } from "@expo/vector-icons";
import { supabase } from "@/lib/supabase/client";
import { useAuth } from "@/providers/AuthProvider";
import { GameDefinition, GameChallengeConfig, SessionMode } from "../types";
import { GameModeSelector } from "./GameModeSelector";

interface Profile {
  id: string;
  full_name: string;
  avatar_url: string | null;
}

interface SendChallengeModalProps {
  /** Whether modal is visible */
  visible: boolean;
  /** Game to challenge for */
  game: GameDefinition;
  /** Callback when modal closes */
  onClose: () => void;
  /** Callback when challenge is sent */
  onSendChallenge: (
    challengedId: string,
    mode: SessionMode,
    config: GameChallengeConfig,
    message?: string
  ) => Promise<void>;
  /** Loading state */
  isLoading?: boolean;
}

/**
 * Modal to send a game challenge
 */
export const SendChallengeModal: React.FC<SendChallengeModalProps> = ({
  visible,
  game,
  onClose,
  onSendChallenge,
  isLoading = false,
}) => {
  const { activeProfile, activeWeekend } = useAuth();

  const [users, setUsers] = useState<Profile[]>([]);
  const [selectedUser, setSelectedUser] = useState<Profile | null>(null);
  const [mode, setMode] = useState<SessionMode>("free");
  const [message, setMessage] = useState("");
  const [config, setConfig] = useState<GameChallengeConfig>({});
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");

  // Fetch available users to challenge
  useEffect(() => {
    if (!visible) return;

    const fetchUsers = async () => {
      setLoadingUsers(true);
      try {
        const { data, error } = await supabase
          .from("profiles")
          .select("id, full_name, avatar_url")
          .neq("id", activeProfile?.id)
          .eq("is_dependent", false)
          .order("full_name");

        if (error) throw error;
        setUsers(data || []);
      } catch (err) {
        console.error("Error fetching users:", err);
      } finally {
        setLoadingUsers(false);
      }
    };

    fetchUsers();
  }, [visible, activeProfile?.id]);

  // Reset state when modal closes
  useEffect(() => {
    if (!visible) {
      setSelectedUser(null);
      setMessage("");
      setConfig({});
      setSearchQuery("");
    }
  }, [visible]);

  // Filter users by search query
  const filteredUsers = users.filter((user) =>
    user.full_name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleSend = async () => {
    if (!selectedUser) return;
    await onSendChallenge(selectedUser.id, mode, config, message || undefined);
  };

  if (!visible) return null;

  return (
    <View className="absolute inset-0 bg-black/50 justify-center items-center p-4 z-50">
      <View className="bg-white dark:bg-gray-800 rounded-2xl w-full max-w-md max-h-[85%] overflow-hidden">
        {/* Header */}
        <View className="flex-row items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
          <View className="flex-row items-center">
            <View
              className="w-10 h-10 rounded-full items-center justify-center"
              style={{ backgroundColor: game.color }}
            >
              <Ionicons name={game.icon as keyof typeof Ionicons.glyphMap} size={22} color="#FFFFFF" />
            </View>
            <View className="ml-3">
              <Text className="font-bold text-gray-900 dark:text-white text-lg">
                Uitdagen
              </Text>
              <Text className="text-sm text-gray-500 dark:text-gray-400">
                {game.name}
              </Text>
            </View>
          </View>
          <TouchableOpacity
            onPress={onClose}
            className="p-2 -mr-2"
            accessibilityLabel="Sluiten"
            accessibilityRole="button"
          >
            <Ionicons name="close" size={24} color="#6B7280" />
          </TouchableOpacity>
        </View>

        <ScrollView className="flex-1 p-4">
          {/* Step 1: Select Opponent */}
          <Text className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Kies tegenstander
          </Text>

          {/* Search */}
          <View className="flex-row items-center bg-gray-100 dark:bg-gray-700 rounded-xl px-3 py-2 mb-3">
            <Ionicons name="search" size={18} color="#6B7280" />
            <TextInput
              className="flex-1 ml-2 text-gray-800 dark:text-white"
              placeholder="Zoek familielid..."
              placeholderTextColor="#9CA3AF"
              value={searchQuery}
              onChangeText={setSearchQuery}
            />
          </View>

          {/* Users List */}
          {loadingUsers ? (
            <View className="items-center py-6">
              <ActivityIndicator size="small" color="#7C3AED" />
            </View>
          ) : (
            <View className="mb-4">
              {filteredUsers.slice(0, 5).map((user) => (
                <TouchableOpacity
                  key={user.id}
                  onPress={() => setSelectedUser(user)}
                  className={`flex-row items-center p-3 rounded-xl mb-2 ${
                    selectedUser?.id === user.id
                      ? "bg-purple-100 dark:bg-purple-900/30 border-2 border-purple-500"
                      : "bg-gray-50 dark:bg-gray-700"
                  }`}
                  accessibilityLabel={`Selecteer ${user.full_name}`}
                  accessibilityRole="radio"
                  accessibilityState={{ checked: selectedUser?.id === user.id }}
                >
                  {user.avatar_url ? (
                    <Image
                      source={{ uri: user.avatar_url }}
                      className="w-10 h-10 rounded-full"
                    />
                  ) : (
                    <View className="w-10 h-10 rounded-full bg-purple-200 dark:bg-purple-800 items-center justify-center">
                      <Text className="text-purple-700 dark:text-purple-300 font-bold">
                        {user.full_name.charAt(0).toUpperCase()}
                      </Text>
                    </View>
                  )}
                  <Text className="flex-1 ml-3 font-medium text-gray-800 dark:text-white">
                    {user.full_name}
                  </Text>
                  {selectedUser?.id === user.id && (
                    <Ionicons name="checkmark-circle" size={24} color="#7C3AED" />
                  )}
                </TouchableOpacity>
              ))}
              {filteredUsers.length === 0 && (
                <Text className="text-center text-gray-500 py-4">
                  Geen familieleden gevonden
                </Text>
              )}
              {filteredUsers.length > 5 && (
                <Text className="text-center text-sm text-gray-400 mt-1">
                  +{filteredUsers.length - 5} meer...
                </Text>
              )}
            </View>
          )}

          {/* Step 2: Game Mode */}
          {selectedUser && (
            <>
              <View className="mt-2 mb-4">
                <GameModeSelector
                  mode={mode}
                  onModeChange={setMode}
                  weekendAvailable={!!activeWeekend}
                />
              </View>

              {/* Chess-specific options */}
              {game.slug === "chess" && (
                <View className="mb-4">
                  <Text className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Tijdscontrole
                  </Text>
                  <View className="flex-row flex-wrap gap-2">
                    {[
                      { value: "none", label: "Geen" },
                      { value: "5min", label: "5 min" },
                      { value: "10min", label: "10 min" },
                      { value: "15+10", label: "15+10" },
                      { value: "30min", label: "30 min" },
                    ].map((option) => (
                      <TouchableOpacity
                        key={option.value}
                        onPress={() =>
                          setConfig({ ...config, time_control: option.value as GameChallengeConfig["time_control"] })
                        }
                        className={`px-4 py-2 rounded-lg ${
                          (config.time_control || "none") === option.value
                            ? "bg-purple-500"
                            : "bg-gray-200 dark:bg-gray-700"
                        }`}
                      >
                        <Text
                          className={`font-medium ${
                            (config.time_control || "none") === option.value
                              ? "text-white"
                              : "text-gray-700 dark:text-gray-300"
                          }`}
                        >
                          {option.label}
                        </Text>
                      </TouchableOpacity>
                    ))}
                  </View>
                </View>
              )}

              {/* Optional message */}
              <View className="mb-4">
                <Text className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Bericht (optioneel)
                </Text>
                <TextInput
                  className="bg-gray-100 dark:bg-gray-700 rounded-xl px-4 py-3 text-gray-800 dark:text-white"
                  placeholder="Denk je dat je kunt winnen? 😏"
                  placeholderTextColor="#9CA3AF"
                  value={message}
                  onChangeText={setMessage}
                  maxLength={100}
                  multiline
                />
              </View>
            </>
          )}
        </ScrollView>

        {/* Footer */}
        <View className="p-4 border-t border-gray-200 dark:border-gray-700">
          <TouchableOpacity
            onPress={handleSend}
            disabled={!selectedUser || isLoading}
            className={`flex-row items-center justify-center py-4 rounded-xl ${
              selectedUser && !isLoading
                ? "bg-purple-600"
                : "bg-gray-300 dark:bg-gray-600"
            }`}
            accessibilityLabel="Verstuur uitdaging"
            accessibilityRole="button"
            accessibilityState={{ disabled: !selectedUser || isLoading }}
          >
            {isLoading ? (
              <ActivityIndicator size="small" color="#FFFFFF" />
            ) : (
              <>
                <Ionicons name="send" size={20} color="#FFFFFF" />
                <Text className="ml-2 font-bold text-white text-lg">
                  Uitdaging versturen
                </Text>
              </>
            )}
          </TouchableOpacity>
        </View>
      </View>
    </View>
  );
};

export default SendChallengeModal;

